// BACS run detail — Wave D.
//
// Per-run breakdown: status, totals, audit fields, included records, and
// the Standard 18 download button. Draft runs can be submitted; draft runs
// can also be voided. Submitted/voided runs are read-only (terminal).

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Send, Trash2 } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { useBacsRunDetail, useSubmitBacsRun, useVoidBacsRun } from './hooks/useBacs';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useToast } from '../../hooks/useToast';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import type { BacsRunStatus } from '../../types/api';
import styles from './BACSRunDetailPage.module.css';

const STATUS_MAP: Record<BacsRunStatus, BadgeVariant> = {
  draft: 'muted',
  submitted: 'success',
  voided: 'danger',
};
const STATUS_LABELS: Record<BacsRunStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  voided: 'Voided',
};

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function downloadStandard18(encId: string, reference: string, onError: () => void) {
  try {
    const res = await apiClient.raw.get<Blob>(endpoints.bacs.exportFile(encId), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reference}.txt`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    onError();
  }
}

export function BACSRunDetailPage(): JSX.Element {
  const { enc_id: encIdParam, id: idParam } = useParams<{ enc_id?: string; id?: string }>();
  const encId = encIdParam ?? idParam;
  const navigate = useNavigate();
  const toast = useToast();
  const detail = useBacsRunDetail(encId);
  const submit = useSubmitBacsRun(encId ?? '');
  const voidRun = useVoidBacsRun(encId ?? '');
  const [voidOpen, setVoidOpen] = useState(false);

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="BACS run" />
        <div className={styles.loading}>
          <Spinner />
        </div>
      </div>
    );
  }

  const run = detail.data?.run;
  if (!run) {
    return (
      <div className={styles.page}>
        <PageHeader title="Run not found" />
        <Card>
          <p>The BACS run you requested could not be loaded.</p>
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/collections/bacs/history')}>
            Back
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Run ${run.reference}`}
        subtitle={
          <span className={styles.subRow}>
            <StatusBadge
              status={run.status}
              statusMap={STATUS_MAP}
              formatLabel={(s) => STATUS_LABELS[s] ?? s}
            />
            <span>· Payment date {formatDate(run.payment_date)}</span>
            <span>· {run.total_records} record(s)</span>
            <span>· {formatMoney(run.total_amount)}</span>
          </span>
        }
        actions={
          <div className={styles.actions}>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/collections/bacs/history')}>
              Back
            </Button>
            {run.status !== 'voided' ? (
              <Button
                variant="secondary"
                icon={Download}
                onClick={() =>
                  void downloadStandard18(run.enc_id, run.reference, () =>
                    toast.error('Download failed.'),
                  )
                }
              >
                Standard 18
              </Button>
            ) : null}
            {run.status === 'draft' ? (
              <>
                <Button
                  variant="primary"
                  icon={Send}
                  onClick={() => submit.mutate()}
                  loading={submit.isPending}
                >
                  Submit
                </Button>
                <Button
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setVoidOpen(true)}
                  loading={voidRun.isPending}
                >
                  Void
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      <div className={styles.grid}>
        <Card title="Audit">
          <dl className={styles.dl}>
            <div>
              <dt>Created</dt>
              <dd>
                {formatDate(run.created_at)}{' '}
                {run.created_by_name ? `by ${run.created_by_name}` : ''}
              </dd>
            </div>
            <div>
              <dt>Submitted</dt>
              <dd>
                {formatDate(run.submitted_at)}{' '}
                {run.submitted_by_name ? `by ${run.submitted_by_name}` : ''}
              </dd>
            </div>
            <div>
              <dt>Voided</dt>
              <dd>
                {formatDate(run.voided_at)}{' '}
                {run.voided_by_name ? `by ${run.voided_by_name}` : ''}
              </dd>
            </div>
            {run.void_reason ? (
              <div>
                <dt>Void reason</dt>
                <dd>{run.void_reason}</dd>
              </div>
            ) : null}
          </dl>
        </Card>

        {run.notes ? (
          <Card title="Notes">
            <p className={styles.notes}>{run.notes}</p>
          </Card>
        ) : null}
      </div>

      <Card title={`Records (${run.records.length})`} flush>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Receipt #</th>
              <th>Reg</th>
              <th>Customer</th>
              <th>Account name</th>
              <th>Sort code</th>
              <th>Account number</th>
              <th className={styles.colNum}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {run.records.map((rec) => (
              <tr key={rec.enc_id}>
                <td>{rec.receipt_reference ?? rec.receipt_unique_id}</td>
                <td>{rec.receipt_car_registration}</td>
                <td>{rec.receipt_customer_name}</td>
                <td>{rec.account_name ?? '—'}</td>
                <td>{rec.sort_code ?? '—'}</td>
                <td>{rec.account_number ?? '—'}</td>
                <td className={styles.colNum}>{formatMoney(rec.payment_amount)}</td>
              </tr>
            ))}
            {run.records.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  No records.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>

      <ConfirmModal
        open={voidOpen}
        title="Void this BACS run?"
        message="Voiding releases the receipts back to the unpaid pool. This action cannot be undone."
        confirmLabel="Void"
        cancelLabel="Cancel"
        variant="danger"
        loading={voidRun.isPending}
        onCancel={() => setVoidOpen(false)}
        onConfirm={() =>
          voidRun.mutate(undefined, {
            onSuccess: () => {
              setVoidOpen(false);
              navigate('/collections/bacs/history');
            },
          })
        }
      />
    </div>
  );
}
